import { toDatePart } from "./schedule-date-time";

// Dev-only. The backend refuses to create an appointment inside an availability
// exception, and equally refuses an exception laid over existing appointments —
// in both directions, on create and on update. Production data does contain such
// overlaps (KIN-1591), so this injects them locally to make the board's stacking
// behaviour testable. Never enable outside local development.
const isEnabled = () =>
  process.env.REACT_APP_MOCK_SCHEDULE_OVERLAPS === "true";

const buildException = ({ doctorSchedule, staffMemberId, index }) => {
  const appointments = doctorSchedule.filter(
    (meeting) => meeting.appointmentType === "consultation"
  );

  if (appointments.length === 0) {
    return null;
  }

  const [first] = appointments;
  const last = appointments[appointments.length - 1];
  const day = toDatePart(first.startTime);

  // Cover the doctor's whole booked stretch, so every card lands on it.
  const startAt = first.startAt;
  const endAt = [last.endAt[0] + 1, 0];
  const pad = (value) => String(value).padStart(2, "0");
  const isRecurring = index % 2 === 1;

  return {
    // ISO, as the backend has sent since KIN-2174.
    startTime: `${day}T${pad(startAt[0])}:${pad(startAt[1])}:00`,
    endTime: `${day}T${pad(endAt[0])}:${pad(endAt[1])}:00`,
    startAt,
    endAt,
    appointmentDate: null,
    appointmentType: "exception",
    patientId: null,
    patient: null,
    staffMemberId,
    practiceServiceId: null,
    practiceService: null,
    serviceName: null,
    durationInMins: null,
    reasonForVisit: null,
    reasonDescription: null,
    appointmentId: null,
    appointmentStatus: "UNAVAILABLE",
    exceptionType: isRecurring ? "Training" : "Annual Leave",
    exceptionReason: "Mocked overlap (REACT_APP_MOCK_SCHEDULE_OVERLAPS)",
    exceptionId: -(1000 + index),
    recurrenceType: isRecurring ? "WEEK" : null,
    recurrenceInterval: isRecurring ? 1 : null,
    recurrenceDaysOfWeek: null,
    exceptionStartDate: `${day}T00:00:00`,
    exceptionEndDate: `${day}T00:00:00`,
    cancellationReason: null,
    consultationType: "IN_PERSON",
    companyName: null,
    cancelledOutcome: null,
    appointmentVideoUrl: null,
    videoAppointmentExtId: null,
    telephoneNumber: null,
    email: null,
    allDay: false,
    recurring: isRecurring,
  };
};

/**
 * Adds an availability exception underneath each doctor's booked appointments,
 * producing the overlap the backend will not let us create for real.
 */
export const withMockedScheduleOverlaps = (staffMembers) => {
  if (!isEnabled() || !Array.isArray(staffMembers)) {
    return staffMembers;
  }

  return staffMembers.map((doctor, index) => {
    const alreadyHasException = doctor.schedule?.some(
      (meeting) => meeting.appointmentType === "exception"
    );

    if (alreadyHasException) {
      return doctor;
    }

    const exception = buildException({
      doctorSchedule: doctor.schedule || [],
      staffMemberId: doctor.staffMemberId,
      index,
    });

    if (!exception) {
      return doctor;
    }

    return { ...doctor, schedule: [exception, ...doctor.schedule] };
  });
};
